/**
 * View Manager Component
 * 
 * A comprehensive management interface for Views with:
 * - List/grid view of all views
 * - Search and filtering capabilities
 * - CRUD operations (Create, Read, Update, Delete)
 * - Bulk operations
 * - View templates and duplication
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem as MenuItemComponent,
  Fab,
  Tooltip,
  Alert,
  Skeleton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  FilterList as FilterIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Folder as FolderIcon,
  AccessTime as RecentIcon,
  Drafts as DraftIcon,
  Archive as ArchiveIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { viewsApiService } from '../../services/views.api';
import {
  View,
  ViewFilter,
  ViewListResponse,
} from '../../types/view.types';
import { UnifiedTag } from '../../types/content.types';
import { tagService } from '../../services/content/tag.service';
import ShareDialog from '../shared/ShareDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../utils/permissions';
import { ContentThumbnailBanner, DocumentIllustration } from '../content/atoms';

const ManagerContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
}));

const ManagerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
}));

const FilterBar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const ViewCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  height: 320,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[6],
    transform: 'translateY(-4px)',
  },
}));

const ViewCardContent = styled(CardContent)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

interface ViewManagerProps {
  organizationId: string;
  teamId?: string;
  onViewSelect?: (view: View) => void;
  onViewEdit?: (view: View) => void;
  onViewCreate?: () => void;
  mode?: 'manage' | 'select';
}

export const ViewManager: React.FC<ViewManagerProps> = ({
  organizationId,
  teamId,
  onViewSelect,
  onViewEdit,
  onViewCreate,
  mode = 'manage',
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();

  // Check permissions
  const { hasPermission } = usePermissions();
  const canCreateView = hasPermission(PERMISSIONS.VIEW_CREATE);
  const canUpdateView = hasPermission(PERMISSIONS.VIEW_UPDATE);
  const canDeleteView = hasPermission(PERMISSIONS.VIEW_DELETE);
  const canShareView = hasPermission(PERMISSIONS.VIEW_SHARE);

  // Data state
  const [views, setViews] = useState<View[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tagsMap, setTagsMap] = useState<Map<string, UnifiedTag>>(new Map());
  
  // UI state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [publicFilter, setPublicFilter] = useState<number | 'all'>('all');
  const [selectedViews, setSelectedViews] = useState<string[]>([]);
  
  // Dialog state
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; view?: View }>({ open: false });
  const [duplicateDialog, setDuplicateDialog] = useState<{ open: boolean; view?: View }>({ open: false });
  const [newViewName, setNewViewName] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareView, setShareView] = useState<View | null>(null);

  // Menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    view?: View;
  } | null>(null);

  // Helper function to get status icon based on view status
  const getStatusIcon = (view: View) => {
    const status = view.status;
    switch (status) {
      case 'draft':
        return <DraftIcon fontSize="small" color="disabled" />;
      case 'private':
        return <PrivateIcon fontSize="small" color="action" />;
      case 'public':
        return <PublicIcon fontSize="small" color="success" />;
      case 'archived':
        return <ArchiveIcon fontSize="small" color="warning" />;
      default:
        return <PrivateIcon fontSize="small" color="action" />;
    }
  };

  // Load views on component mount and filter changes
  useEffect(() => {
    loadViews();
  }, [organizationId, teamId, searchQuery, publicFilter]);

  // Load tags when team changes
  useEffect(() => {
    if (teamId) {
      loadTags();
    }
  }, [organizationId, teamId]);

  const loadTags = async () => {
    if (!organizationId || !teamId) return;

    try {
      const tags = await tagService.listUnifiedTags({
        organization_id: organizationId,
        team_id: teamId,
        scope: 'team',
      });

      // Create a map for quick lookups
      const map = new Map<string, UnifiedTag>();
      tags.forEach(tag => map.set(tag.id, tag));
      setTagsMap(map);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const loadViews = async () => {
    setLoading(true);
    setError(null);

    try {
      const baseFilters: ViewFilter = {
        organization_id: organizationId,
        limit: 100, // Load more views for management interface
      };

      if (teamId) baseFilters.team_id = teamId;
      if (searchQuery) baseFilters.search = searchQuery;

      let allViews: View[] = [];

      if (publicFilter === 'all') {
        // When showing all views, fetch all status types and combine
        try {
          const statusPromises = [1, 2, 3, 4].map(statusValue =>
            viewsApiService.listViews({ ...baseFilters, status: statusValue })
          );

          const responses = await Promise.all(statusPromises);

          // Combine and deduplicate by ID
          const viewMap = new Map<string, View>();
          responses.forEach(response => {
            (response.data || []).forEach(view => {
              viewMap.set(view.id, view);
            });
          });

          allViews = Array.from(viewMap.values());
        } catch (err) {
          // If parallel requests fail, try without status filter
          console.warn('Failed to fetch views by status separately, trying combined request:', err);
          const response = await viewsApiService.listViews(baseFilters);
          allViews = response.data || [];
        }
      } else {
        // User has explicitly filtered by status (publicFilter is now a number)
        const response = await viewsApiService.listViews({
          ...baseFilters,
          status: publicFilter as number,
        });
        allViews = response.data || [];
      }

      // Sort by updatedAt descending (most recent first)
      allViews.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

      setViews(allViews);
    } catch (err) {
      console.error('Error loading views:', err);
      setError('Failed to load views');
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (view: View) => {
    if (mode === 'select') {
      onViewSelect?.(view);
    } else {
      onViewEdit?.(view);
    }
  };

  const handleViewAction = (action: string, view: View) => {
    switch (action) {
      case 'edit':
        onViewEdit?.(view);
        break;
      case 'duplicate':
        setDuplicateDialog({ open: true, view });
        setNewViewName(`${view.name} (Copy)`);
        break;
      case 'delete':
        setDeleteDialog({ open: true, view });
        break;
      case 'view':
        onViewSelect?.(view);
        break;
      case 'share':
        // Only allow sharing PUBLIC views
        if (view.status !== 'public') {
          showNotification(
            'Only public views can be shared. Please change the view status to "Public" first.',
            'warning'
          );
          break;
        }
        setShareView(view);
        setShareDialogOpen(true);
        break;
    }
    setContextMenu(null);
  };

  const handleDuplicate = async () => {
    if (!duplicateDialog.view) return;

    try {
      const duplicatedView = await viewsApiService.duplicateView(
        duplicateDialog.view.id,
        { name: newViewName }
      );
      
      setViews(prev => [duplicatedView, ...prev]);
      setDuplicateDialog({ open: false });
      setNewViewName('');
    } catch (error) {
      console.error('Error duplicating view:', error);
      setError('Failed to duplicate view');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.view || !teamId) return;

    try {
      await viewsApiService.deleteView(deleteDialog.view.id, teamId);
      setViews(prev => prev.filter(v => v.id !== deleteDialog.view?.id));
      setDeleteDialog({ open: false });
    } catch (error) {
      console.error('Error deleting view:', error);
      setError('Failed to delete view');
    }
  };

  return (
    <ManagerContainer>
      {/* Header */}
      <ManagerHeader sx={{ justifyContent: 'flex-end' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}>
            <IconButton onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
              {viewMode === 'grid' ? <ListViewIcon /> : <GridViewIcon />}
            </IconButton>
          </Tooltip>

          {mode === 'manage' && canCreateView && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onViewCreate}
              size="large"
            >
              Create View
            </Button>
          )}
        </Box>
      </ManagerHeader>

      {/* Filter Bar */}
      <FilterBar elevation={1}>
        <TextField
          size="small"
          placeholder="Search views..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={publicFilter === 'all' ? 'all' : publicFilter.toString()}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'all') {
                setPublicFilter('all');
              } else {
                setPublicFilter(parseInt(value, 10));
              }
            }}
            label="Status"
          >
            <MenuItemComponent value="all">All Statuses</MenuItemComponent>
            <MenuItemComponent value="1">Draft</MenuItemComponent>
            <MenuItemComponent value="2">Private</MenuItemComponent>
            <MenuItemComponent value="3">Public</MenuItemComponent>
            <MenuItemComponent value="4">Archived</MenuItemComponent>
          </Select>
        </FormControl>

        <Box flex={1} />

        <Typography variant="body2" color="text.secondary">
          {views.length} view{views.length !== 1 ? 's' : ''}
        </Typography>
      </FilterBar>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Views Content */}
      <Box flex={1} overflow="auto" sx={{ pt: 1 }}>
        {loading ? (
          <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" height={32} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} width="60%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : views.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="60%"
            textAlign="center"
          >
            <FolderIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No views found
            </Typography>
            <Typography variant="body2" color="text.disabled" paragraph>
              {searchQuery || publicFilter !== 'all'
                ? 'Try adjusting your filters to see more results.'
                : 'Create your first view to get started with curated content experiences.'}
            </Typography>
            {mode === 'manage' && canCreateView && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onViewCreate}
                size="large"
              >
                Create Your First View
              </Button>
            )}
          </Box>
        ) : viewMode === 'grid' ? (
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {views.map((view) => {
              const showKebab =
                mode === 'manage' && (canUpdateView || canDeleteView || canShareView);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={view.id}>
                  <ViewCard
                    onClick={() => handleViewClick(view)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({
                        mouseX: e.clientX - 2,
                        mouseY: e.clientY - 4,
                        view,
                      });
                    }}
                  >
                    <ContentThumbnailBanner
                      content={{
                        id: view.id,
                        name: view.name,
                        thumbnailUrl:
                          view.thumbnail &&
                          /^https?:\/\//i.test(view.thumbnail) &&
                          !/^https?:\/\/[^/]+\/api\//i.test(view.thumbnail)
                            ? view.thumbnail
                            : undefined,
                      }}
                      fileType="view"
                      height={130}
                      hoverHeight={130}
                      illustrationSize="normal"
                    />
                    {showKebab && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenu({
                            mouseX: e.clientX - 2,
                            mouseY: e.clientY - 4,
                            view,
                          });
                        }}
                        sx={(theme) => ({
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          zIndex: 2,
                          width: 28,
                          height: 28,
                          background:
                            theme.palette.mode === 'light'
                              ? 'rgba(255,255,255,.92)'
                              : 'rgba(15,30,51,.92)',
                          color: theme.palette.text.primary,
                          backdropFilter: 'blur(6px)',
                          boxShadow: '0 1px 2px rgba(15,23,42,.06)',
                          transition: 'transform 0.15s ease, background 0.15s ease',
                          '&:hover': {
                            background:
                              theme.palette.mode === 'light'
                                ? 'rgba(255,255,255,.98)'
                                : 'rgba(15,30,51,.98)',
                            transform: 'scale(1.08)',
                          },
                        })}
                      >
                        <MoreIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}

                    <ViewCardContent>
                      <Typography variant="h6" component="h3" noWrap sx={{ mb: 0.5 }}>
                        {view.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          mb: 1,
                        }}
                      >
                        {view.description || 'No description'}
                      </Typography>

                      {view.tagIds && view.tagIds.length > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5} mb={1} flexWrap="wrap">
                          {view.tagIds.slice(0, 3).map((tagId) => {
                            const tag = tagsMap.get(tagId);
                            return (
                              <Chip
                                key={tagId}
                                size="small"
                                label={tag?.name || tagId}
                                variant="outlined"
                                sx={{
                                  borderColor: tag?.color || undefined,
                                  color: tag?.color || undefined,
                                }}
                              />
                            );
                          })}
                          {view.tagIds.length > 3 && (
                            <Chip
                              size="small"
                              label={`+${view.tagIds.length - 3} more`}
                              variant="outlined"
                              color="default"
                            />
                          )}
                        </Box>
                      )}

                      <Box sx={{ flex: 1 }} />

                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(view)}
                        <Typography variant="caption" color="text.disabled">
                          Updated {new Date(view.updatedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </ViewCardContent>
                  </ViewCard>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <List>
            {views.map((view) => (
              <React.Fragment key={view.id}>
                <ListItemButton
                  onClick={() => handleViewClick(view)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({
                      mouseX: e.clientX - 2,
                      mouseY: e.clientY - 4,
                      view,
                    });
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 88 }}>
                    <Box
                      sx={(theme) => ({
                        width: 72,
                        height: 52,
                        borderRadius: 1,
                        backgroundColor: theme.palette.fileTints.view,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        overflow: 'hidden',
                      })}
                    >
                      <DocumentIllustration fileType="view" size="compact" />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">{view.name}</Typography>
                        {getStatusIcon(view)}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {view.description || 'No description'}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Updated {new Date(view.updatedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                  {mode === 'manage' && (canUpdateView || canDeleteView || canShareView) && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setContextMenu({
                          mouseX: e.clientX - 2,
                          mouseY: e.clientY - 4,
                          view,
                        });
                      }}
                    >
                      <MoreIcon />
                    </IconButton>
                  )}
                </ListItemButton>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItemComponent onClick={() => handleViewAction('view', contextMenu?.view!)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          View
        </MenuItemComponent>
        {mode === 'manage' && canUpdateView && (
          <MenuItemComponent onClick={() => handleViewAction('edit', contextMenu?.view!)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit
          </MenuItemComponent>
        )}
        {mode === 'manage' && canCreateView && (
          <MenuItemComponent onClick={() => handleViewAction('duplicate', contextMenu?.view!)}>
            <ListItemIcon>
              <DuplicateIcon fontSize="small" />
            </ListItemIcon>
            Duplicate
          </MenuItemComponent>
        )}
        {mode === 'manage' && canShareView && contextMenu?.view?.status === 'public' && (
          <MenuItemComponent onClick={() => handleViewAction('share', contextMenu?.view!)}>
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            Share
          </MenuItemComponent>
        )}
        {mode === 'manage' && canDeleteView && (
          <>
            <Divider />
            <MenuItemComponent onClick={() => handleViewAction('delete', contextMenu?.view!)}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              Delete
            </MenuItemComponent>
          </>
        )}
      </Menu>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialog.open} onClose={() => setDuplicateDialog({ open: false })}>
        <DialogTitle>Duplicate View</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New View Name"
            fullWidth
            variant="outlined"
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialog({ open: false })}>Cancel</Button>
          <Button onClick={handleDuplicate} variant="contained" disabled={!newViewName.trim()}>
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })}>
        <DialogTitle>Delete View</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.view?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for mobile */}
      {mode === 'manage' && canCreateView && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'block', sm: 'none' },
          }}
          onClick={onViewCreate}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Share Dialog */}
      {shareView && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setShareView(null);
          }}
          itemId={shareView.id}
          itemType="view"
          itemTitle={shareView.name}
        />
      )}
    </ManagerContainer>
  );
};

export default ViewManager;